import { observer } from 'mobx-react'

import Button from '@/component/atoms/Button/Button/Button'
import IconButton from '@/component/atoms/Button/IconButton/IconButton'
import { Switch } from '@/component/atoms/Switch/Switch'
import DatePicker from '@/component/molecules/DatePicker/DatePicker'
import Tooltip from '@/component/molecules/Tooltip/Tooltip'

import { SCHEDULE_STRINGS } from '../_constants/scheduleStrings'
import type { ScheduleFormApi } from '../_service/ScheduleForm'
import { formatScheduleDateTime } from '../_utils/scheduleListFormat'
import ScheduleTimePicker from './ScheduleTimePicker'
import S from './ScheduleToggleRow.style'

const TOGGLE_ID = 'worksheet-assign-schedule-toggle'

type Props = {
  form: ScheduleFormApi
  /** 현재 모달에서 선택된 학생 수. 100명 한도 판정에 쓴다 */
  selectedCount: number
  onOpenList?: () => void
  /** 토글 변경을 가로챈다. 예약 있음 모드에서 OFF 얼럿을 띄우는 호출부가 쓴다. 없으면 form.setEnabled */
  onToggle?: (next: boolean) => void
}

const ScheduleToggleRow = ({ form, selectedCount, onOpenList, onToggle }: Props) => {
  const limitError = form.getLimitError(selectedCount)
  // 새 예약은 한도를 넘으면 토글을 켤 수 없고 켜져 있던 상태도 꺼진 것으로 취급한다.
  // 이미 걸린 예약은 그대로 살아 있으므로 토글을 유지하고 안내만 보여준다
  const hasExisting = !!form.existingAt
  const scheduling = form.enabled && (hasExisting || !limitError)
  const showReservedText = !!form.existingAt && !form.editingTime

  return (
    <div css={S.row}>
      <div className="toggle">
        <Switch
          id={TOGGLE_ID}
          size="s"
          checked={scheduling}
          disabled={!hasExisting && !!limitError}
          onCheckedChange={onToggle ?? form.setEnabled}
        />
        <div className="label-group">
          <label className="label" htmlFor={TOGGLE_ID}>
            {SCHEDULE_STRINGS.toggleLabel()}
          </label>
          <Tooltip
            size={18}
            iconType="info"
            Content={<span css={S.tooltip}>{SCHEDULE_STRINGS.tooltip()}</span>}
          />
        </div>
      </div>
      <div className="controls">
        {limitError && <p className="error">{limitError}</p>}
        {scheduling && showReservedText && form.existingAt && (
          <div className="fields reserved-fields">
            <span className="reserved">
              {formatScheduleDateTime(form.existingAt)} {SCHEDULE_STRINGS.reservedSuffix}
            </span>
            <IconButton
              icon="edit"
              size="xs"
              variant="ghost"
              aria-label={SCHEDULE_STRINGS.editTime}
              onClick={form.startEditingTime}
            />
          </div>
        )}
        {scheduling && !showReservedText && (
          <div className="fields">
            <DatePicker
              css={S.datePicker}
              date={form.scheduledAt}
              setDate={form.setDay}
              minDate={form.minDate}
              maxDate={form.maxDate}
              position="topLeft"
            />
            <ScheduleTimePicker
              value={form.scheduledAt}
              onChange={form.setSlot}
              isSlotSelectable={form.isSlotSelectable}
            />
            {form.deadlineError && <p className="error">{form.deadlineError}</p>}
          </div>
        )}
      </div>
      <Button
        size="m"
        variant="ghost"
        color="neutral"
        rightIcon="chevron_right"
        disabled={!onOpenList}
        onClick={onOpenList}
      >
        {SCHEDULE_STRINGS.openList}
      </Button>
    </div>
  )
}

export default observer(ScheduleToggleRow)
